import React from 'react';
import { Form, Select, InputNumber, Button, Switch, Input } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { type FormInstance } from 'antd/es/form';
import { type FormListFieldData } from 'antd/es/form/FormList';
import { currencyAmountValidator } from '../../../../../common/components/MultipleOfValidator';

const { Option } = Select;

interface ServiceItemValue {
    serviceCategoryId?: number;
    serviceCategoryItemIds?: number[];
}

interface CompanyServiceItemRowProps {
    field: FormListFieldData;
    itemName: number;
    totalItems: number;
    serviceName: number;
    categoryName: number;
    form: FormInstance;
    removeItem: (index: number) => void;
    isLoadingServiceCategories: boolean;
    serviceCategoriesOptions: { label: string; labelAr?: string; value: number }[] | undefined;
    serviceCategoryItemsByCategory: Record<number, { label: string; labelAr?: string; value: number }[]>;
    currencyISOCode: string | undefined;
    updateCoverageAmount: (form: FormInstance, categoryIndex: number) => void;
}

const CompanyServiceItemRow: React.FC<CompanyServiceItemRowProps> = ({
    field,
    itemName,
    totalItems,
    serviceName,
    categoryName,
    form,
    removeItem,
    isLoadingServiceCategories,
    serviceCategoriesOptions,
    serviceCategoryItemsByCategory,
    currencyISOCode,
    updateCoverageAmount,
}) => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';
    const companyId = Form.useWatch(['companyId'], form);
    const isCompanySelected = Number(companyId || 0) > 0;

    const serviceCategoryId = Form.useWatch(['planCategories', categoryName, 'serviceTypes', serviceName, 'serviceItems', itemName, 'serviceCategoryId'], form) as number | undefined;
    const isAmountCoverage = Form.useWatch(['planCategories', categoryName, 'serviceTypes', serviceName, 'serviceItems', itemName, 'isAmountCoverage'], form);
    const serviceCategoryItems = serviceCategoryId ? (serviceCategoryItemsByCategory[Number(serviceCategoryId)] || []) : [];

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '32px 1.1fr 1.1fr 130px 130px auto',
                gap: 12,
                alignItems: 'start',
                padding: '10px 12px',
                background: itemName % 2 === 0 ? '#fafbfd' : '#ffffff',
                borderBottom: '1px solid #f0f2f5',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f6f8fb';
                e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #e1e8f0';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = itemName % 2 === 0 ? '#fafbfd' : '#ffffff';
                e.currentTarget.style.boxShadow = 'none';
            }}
        >
            <div
                style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: 'linear-gradient(135deg, #0f85ae 0%, #61aeb2 100%)',
                    color: serviceCategoryId ? '#fff' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                    marginTop: 8,
                }}
            >
                {itemName + 1}
            </div>

            <Form.Item
                {...field}
                name={[itemName, 'serviceCategoryId']}
                style={{ marginBottom: 0 }}
                rules={[
                    { required: true, message: t('Missing main service') },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value) return Promise.resolve();

                            const serviceItems = getFieldValue([
                                'planCategories',
                                categoryName,
                                'serviceTypes',
                                serviceName,
                                'serviceItems',
                            ]) as ServiceItemValue[] | undefined;

                            const duplicateFound = serviceItems?.some((item, index) => {
                                if (!item || index === itemName) {
                                    return false;
                                }
                                return item.serviceCategoryId === value;
                            });

                            if (duplicateFound) {
                                return Promise.reject(new Error(t('This main service is already added')));
                            }

                            return Promise.resolve();
                        },
                    }),
                ]}
            >
                <Select
                    placeholder={t('Select main service')}
                    loading={isLoadingServiceCategories}
                    disabled={!isCompanySelected}
                    showSearch
                    size="middle"
                    style={{ width: '100%' }}
                    filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    onChange={() => {
                        form.setFieldsValue({
                            planCategories: {
                                [categoryName]: {
                                    serviceTypes: {
                                        [serviceName]: {
                                            serviceItems: {
                                                [itemName]: {
                                                    serviceCategoryItemIds: [],
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        });
                    }}
                >
                    {serviceCategoriesOptions?.map((item) => (
                        <Option
                            key={item.value}
                            value={item.value}
                            label={isArabic && item.labelAr ? item.labelAr : item.label}
                        >
                            {isArabic && item.labelAr ? item.labelAr : item.label}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item
                {...field}
                name={[itemName, 'serviceCategoryItemIds']}
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: t('Missing sub service') }]}
            >
                <Select
                    mode="multiple"
                    placeholder={t('Select sub service')}
                    loading={isLoadingServiceCategories}
                    disabled={!isCompanySelected || !serviceCategoryId}
                    showSearch
                    size="middle"
                    maxTagCount={2}
                    maxTagTextLength={20}
                    style={{ width: '100%' }}
                    filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                >
                    {serviceCategoryItems.map((item) => (
                        <Option
                            key={item.value}
                            value={item.value}
                            label={isArabic && item.labelAr ? item.labelAr : item.label}
                        >
                            {isArabic && item.labelAr ? item.labelAr : item.label}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item
                {...field}
                name={[itemName, 'coShare']}
                style={{ marginBottom: 0 }}
                rules={[
                    () => ({
                        validator(_, value) {
                            if (isAmountCoverage) {
                                return Promise.resolve();
                            }

                            if (!isAmountCoverage && (value === undefined || value === null || value === '')) {
                                return Promise.reject(new Error(t('coSharePercentageMandatory')));
                            }

                            return Promise.resolve();
                        },
                    }),
                ]}
            >
                <InputNumber min={1} max={50} precision={0} style={{ width: '100%' }} size="large" placeholder={t('Co-Share (%)')} />
            </Form.Item>

            {isAmountCoverage ? (
                <Form.Item
                    {...field}
                    name={[itemName, 'amount']}
                    style={{ marginBottom: 0 }}
                    rules={[
                        { required: true, message: t('pleaseEnterAmount') },
                        currencyAmountValidator(currencyISOCode, ''),
                    ]}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        size="large"
                        min={1}
                        max={10000000}
                        placeholder={t('Amount')}
                        onChange={() => updateCoverageAmount(form, categoryName)}
                    />
                </Form.Item>
            ) : (
                <div />
            )}

            {totalItems !== 1 ? (
                <Button
                    danger
                    type="text"
                    size="small"
                    icon={<DeleteOutlined style={{ color: '#dc2626' }} />}
                    onClick={() => {
                        removeItem(field.name);
                        setTimeout(() => {
                            updateCoverageAmount(form, categoryName);
                        }, 0);
                    }}
                    title={t('Delete')}
                    style={{ marginTop: 4, opacity: 0.6, transition: 'opacity 0.2s ease' }}
                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
                />
            ) : (
                <div style={{ width: 24 }} />
            )}

            <Form.Item
                {...field}
                name={[itemName, 'isAmountCoverage']}
                label={t('isAmountCovered')}
                valuePropName="checked"
                hidden
            >
                <Switch
                    onChange={(checked) => {
                        if (checked) {
                            form.setFields([
                                {
                                    name: [
                                        'planCategories',
                                        categoryName,
                                        'serviceTypes',
                                        serviceName,
                                        'serviceItems',
                                        itemName,
                                        'coShare',
                                    ],
                                    errors: [],
                                },
                            ]);
                        } else {
                            form.validateFields([
                                'planCategories',
                                categoryName,
                                'serviceTypes',
                                serviceName,
                                'serviceItems',
                                itemName,
                                'coShare',
                            ]);
                        }
                    }}
                />
            </Form.Item>

            <Form.Item
                {...field}
                name={[itemName, 'percentage']}
                label={t('Percentage')}
                required
                hidden
            >
                <Input type="hidden" />
            </Form.Item>
        </div>
    );
};

export default CompanyServiceItemRow;
